<template>
  <div class="page">
    <NavigationBar :hide-back="inAppTab" v-if="!roomChatOpen && platform !== 'PC'">
      <template #title>
        <div class="nav-title">
          <van-loading v-if="navLoading" color="#333333" size="20px" />
          <span class="nav-text">{{ $t(navLoading ? 'message.loading' : 'message.title') }}</span>
        </div>
      </template>
      <template #right>
        <button @click="goFeedback">
          <img src="@/assets/svg-icons/feedback.svg" />
        </button>
        <button @click="doClear">
          <img src="@/assets/svg-icons/clear.svg" />
        </button>
      </template>
    </NavigationBar>
    <div class="page-content-wrapper" :style="{
      paddingTop:
        roomChatOpen || platform === 'PC'
          ? `${statusBarHeight}px`
          : `${pagePaddingTop || envStore.navigationBarHeight}px`,
    }">
      <div class="page-content" :class="{ 'page-content-pc': platform === 'PC', 'page-content-room': roomChatOpen }">
        <!-- push -->
        <PushNotification :fatherShow="!roomChatOpen"></PushNotification>
        <!-- top -->
        <ContentWrapper class="top-wrapper" :loading="pageLoading" :error="pageError" @reload="refreshData">
          <div v-if="platform !== 'PC'" v-show="!roomChatOpen" class="top">
            <!-- 物流 -->
            <div class="btn-order" @click="goDeliveryMessageList">
              <div class="icon-area">
                <img class="icon" src="@/assets/svg-icons/order.svg" />
                <div class="badge" v-if="hasUnreadMessage">
                  {{ orderUnreadCount }}
                </div>
              </div>
              <h3 class="title">{{ $t("message.order") }}</h3>
              <span class="desc">{{ $t("message.orderDesc") }}</span>
            </div>
            <!-- 优惠券 -->
            <div class="btn-promo" @click="goCouponList">
              <div class="icon-area">
                <img class="icon" src="../assets/svg-icons/promo.svg" />
              </div>
              <h3 class="title">{{ $t("message.promo") }}</h3>
              <span class="desc" v-html="$t('message.promoDesc', {
                count: `<b>${couponTotalCount}</b>`,
              })
                "></span>
            </div>
          </div>
          <div v-if="platform === 'PC'" class="pc-top-list" v-show="!roomChatOpen">
            <PCItem :itemData="{
              icon: orderIcon,
              unreadNum: orderUnreadCount,
              title: $t('message.order'),
              content: $t('message.orderDesc'),
            }" @click="goDeliveryMessageList" />
            <PCItem :itemData="{
              icon: promoIcon,
              unreadNum: 0,
              title: $t('message.promo'),
              content: $t('message.promoDesc', {
                count: `<b>${couponTotalCount}</b>`,
              }),
            }" @click="goCouponList" />
          </div>
        </ContentWrapper>
        <!-- 消息盒子 -->
        <div class="message-box" :class="{ 'message-list': !roomChatOpen }">
          <div v-if="userStore.isLogin && (newinStore.marketingData || aiShoppingStore.entryVisible) && !roomChatOpen"
            class="out-room-list">
            <AICustomer v-if="aiShoppingStore.entryVisible" />
            <OutItem v-if="newinStore.marketingData" :newin-data="newinStore.marketingData" />
          </div>
          <ImMessageBox ref="imMessage" :page-top-height="homeTopHeight" :page-padding-top="pagePaddingTop"
            :connect-params="$route.query" :safearea="!inAppTab" @ready="onIMReady"
            :auto-register-loading="userStore.autoRegisterLoading" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, provide, watch, computed, onMounted, onActivated, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";
import { showToast } from "vant";
import {
  navigateTo,
  logAFEvent,
  updateMessageUnreadCount,
  jsBridge,
  EVENT_VENDOR_CALLBACK,
} from "@voghion/native-api";
import { useEnvStore } from "@/states/env";
import { useUserStore } from "@/states/user";
import { useMsgStore } from "@/states/msg";
import { useNotifyStore } from "@/states/notify";
import { useNewinStore } from "@/states/newin";
import { useRouterAdv } from "@/composables/use-router-adv";
import { safeWatch } from "@/composables/use-vue-adv";
import { useSetting } from "@/composables/use-setting";
// import { wsLangSetting } from '@/composables/use-ws-lang'
import { getFeedBackUrl } from "@/utils/env";
import { isSingleRoom, getResource } from "@/utils/msgFilter";
import ImMessageBox from "@/components/ImMessageBox/ImMessageBox.vue";
import ContentWrapper from "@/components/ContentWrapper.vue";
import NavigationBar from "@/components/NavigationBar.vue";
import PushNotification from "@/components/PushNotification.vue";
import OutItem from "@/components/ImMessageBox/OutRoomList/Item.vue";
import PCItem from "@/components/ImMessageBox/PCList/Item.vue";
import {
  getCouponData,
  getDeliveryMessageUnread,
  clearDeliveryMessageUnread,
  getDeliveryMessageList,
} from "@/api/index";
import orderIcon from "@/assets/svg-icons/order.svg";
import promoIcon from "@/assets/svg-icons/promo.svg";
import AICustomer from "@/components/ImMessageBox/OutRoomList/AICustomer.vue";
import { cacheRooms } from "@/utils/cache";
import { useAiShoppingStore } from "@/states/ai-shopping";
import { RegisterType } from "@/components/sign-in/constant";
import { compareVersion, Env } from "@voghion/fe-util";

const PAGE_NAME = "messagePage";
provide("pageName", PAGE_NAME);
provide("goodsSpmName", "messageRecomGoods");
provide("goodsSpm", "01010430201");

const { t } = useI18n();
const envStore = useEnvStore();
const msgStore = useMsgStore();
const userStore = useUserStore();
const notifyStore = useNotifyStore();
const newinStore = useNewinStore();
const routerAdv = useRouterAdv();
const { getClientInfo } = useSetting();
const aiShoppingStore = useAiShoppingStore()
// const _wsLangSetting = wsLangSetting()
const { platform, pageFrom } = getResource();

const { pagePaddingTop, inAppTab, statusBarHeight } = storeToRefs(envStore);
const { rooms } = storeToRefs(msgStore);
const checkLogin = userStore.checkLogin;
const autoRegister = userStore.autoRegister;
const env = Env.getInstance()
// const finalStyle = computed(() => ({ paddingTop: `${pagePaddingTop.value}px` }));

const clientInfo = getClientInfo();

const imMessage = ref<any>(null);
const homeTopHeight = ref(0);
const pageReady = ref(false);
const roomChatOpen = computed(() => {
  return isSingleRoom();
});

/**
 * 是否有未读消息
 */
const hasUnreadMessage = ref(false);
const orderUnreadCount = ref(0);
/**
 * 可用的优惠券数量
 */
const couponTotalCount = ref(0);

const chatUnreadCount = computed(() => {
  return rooms.value.reduce(
    (total, room) => (room.unreadCount || 0) + total,
    0,
  );
});

// const newinData = computed(() => {
//   return newinStore.marketingData;
// });

const unreadAll = computed(() => {
  return (
    chatUnreadCount.value +
    orderUnreadCount.value +
    (notifyStore.notify?.messageBoxNotifyVo?.unreadNum || 0) +
    (notifyStore.notifyVip?.messageBoxNotifyVo?.unreadNum || 0)
  );
});

watch(unreadAll, (newVal) => {
  if (userStore.isLogin) {
    updateMessageUnreadCount({ value: newVal > 0 ? Number(newVal) : 0 });
  }
});

// const onClose = () => {
//   jsBridge.callPromise({noResponse: true}, EVENT_VENDOR_CALLBACK, {
//     key: "close",
//   });
// };

/**
 * 跳转物流信息列表页
 */
const goDeliveryMessageList = () => {
  logAFEvent({
    event: "clickSPM",
    spm: "01010430101",
    spmName: "messageOrder",
  });
  checkLogin({ showPcLogin: true }).then(
    () => {
      hasUnreadMessage.value = false;
      orderUnreadCount.value = 0;
      if (platform === "PC") {
        getDeliveryMessageList({
          pageNow: 1,
          pageSize: 10,
        });
        jsBridge.callPromise({ noResponse: true }, EVENT_VENDOR_CALLBACK, {
          key: "order",
        });
      } else {
        routerAdv.navigateTo("/order-message");
      }
    },
    () => { },
  );
};
/**
 * 跳转优惠券列表页
 */
const goCouponList = () => {
  logAFEvent({
    event: "clickSPM",
    spm: "01010430102",
    spmName: "messagePromo",
  });
  checkLogin({ showPcLogin: true }).then(
    () => {
      if (platform === "PC") {
        jsBridge.callPromise({ noResponse: true }, EVENT_VENDOR_CALLBACK, {
          key: "coupon",
        });
      } else {
        navigateTo({ url: `voghion://approuter/coupon` });
      }
    },
    () => { },
  );
};

const pageError = ref(null);
const pageLoading = ref(false);

const navLoading = computed(() => {
  return (pageLoading.value || msgStore.loadingRooms) && !msgStore.sdkLoadingRooms
})

/**
 * 查询消息
 */
const fetchMessageData = async () => {
  let data = null;
  let error: any = null;
  try {
    const res = await getDeliveryMessageUnread();
    //@ts-ignore
    if (res && res.rtn_code === "0000") {
      hasUnreadMessage.value = res.data.unreadCount > 0;
      orderUnreadCount.value = res.data.unreadCount;

      data = res.data;
    } else {
      error = new Error("server error");
    }
  } catch (err) {
    error = err;
  }
  return {
    data,
    error,
  };
};
/**
 * 查询优惠券
 */
const fetchCouponData = async () => {
  let data = null,
    error: any = null;
  try {
    const res = await getCouponData({
      pageNow: 1,
      pageSize: 1,
      status: 10,
    });
    //@ts-ignore
    if (res && res.rtn_code === "0000") {
      couponTotalCount.value = res.data.rowCount;
      data = res.data;
    } else {
      // error = new Error('server error')
    }
  } catch (err) {
    error = err;
  }
  return {
    data,
    error,
  };
};
const refreshData = async () => {
  pageLoading.value = true;
  const resArr = await Promise.all([fetchMessageData(), fetchCouponData()]);
  let hasError = false;
  for (const res of resArr) {
    if (res.error) {
      pageError.value = res.error;
      hasError = true;
      break;
    }
  }
  if (!hasError) {
    pageError.value = null;
  }
  pageLoading.value = false;
  pageReady.value = true;
};
const goFeedback = () => {
  routerAdv.navigateTo(getFeedBackUrl());
};
const doClear = () => {
  hasUnreadMessage.value = false;
  showToast({
    message: t("message.clearMessageSuccess"),
  });
  orderUnreadCount.value = 0;
  clearDeliveryMessageUnread().then(
    () => { },
    () => { },
  );
  notifyStore.clearNotifyUnread(true);
  notifyStore.clearNotifyUnread(false);
  // newinStore.clearMarketingUnread();
  // 清除所有IM消息
  imMessage.value?.doClearAllChat();

  // _wsLangSetting.clear()
};
// 切换用户刷新页面
safeWatch(
  () => {
    return userStore.userToken;
  },
  (val, oldValue) => {
    msgStore.closeRoom();
    let refreshPage = false;
    if (val) {
      if (!oldValue) {
        // 登录
        refreshData();
        newinStore.getMarketing();
      } else {
        // 切换用户
        refreshPage = true;
      }
    } else {
      if (oldValue) {
        // 退出
        refreshPage = true;
      } else {
        hasUnreadMessage.value = false;
        couponTotalCount.value = 0;
      }
    }
    if (refreshPage) {
      cacheRooms(null)
      aiShoppingStore.clearCache()
      // newinStore.clearCache()
      setTimeout(() => {
        window.location.reload();
      }, 50);
    }
  },
);
const trackPageView = () => {
  if (currentRoomId.value) {
    logAFEvent({
      event: "showSPM",
      spm: "01010470000",
      spmName: "chatPage",
      params: {
        conversationId: currentRoomId.value,
        url: window.location.href,
      },
    });
  } else {
    logAFEvent({
      event: "showSPM",
      spm: "01010430000",
      spmName: PAGE_NAME,
      params: {
        url: window.location.href,
      },
    });
  }
};
const currentRoomId = ref(0);
const pageShow = ref(false);
const onIMReady = (conversationId: number) => {
  currentRoomId.value = conversationId;
  if (!pageShow.value) {
    pageShow.value = true;
    trackPageView();
  }
};

const checkAutoLogin = () => {
  if (!userStore.isLogin) {
    const currentAppVersion = clientInfo.appVersion
    const checkAndroid = env.isAndroid && compareVersion(currentAppVersion, '2.3.8') >= 0
    const checkIos = env.isIOS && compareVersion(currentAppVersion, '2.2.5') >= 0
    
    // 判断自动注册版本
    const appCheck = checkAndroid || checkIos
    // 判断自动注册入口：只有app内的，tab客服页和me页，需要加自动注册
    const checkFrom = inAppTab.value || pageFrom === 'me'

    if (appCheck && checkFrom) {
      autoRegister('', RegisterType.AUTO)
    } else {
      checkLogin()
    }
  }
}

onMounted(() => {
  if (userStore.userToken) {
    refreshData();
  }
  
  // app内未登录时，检查是否需要自动注册
  checkAutoLogin();
});


onActivated(() => {
  if (userStore.userToken) {
    newinStore.getMarketing();
  }
  notifyStore.getNotify();
  if (pageShow.value) {
    trackPageView();
  }
});
</script>

<style lang="scss" scoped>
.nav-title {
  display: flex;
  align-items: center;

  .nav-text {
    margin-left: 6px;
    font-weight: 600;
    font-size: 18px;
    color: #333333;
    line-height: 25px;
  }
}

.page {
  width: min(500px, 100%);
  margin: 0 auto;

  .page-content-wrapper {
    height: 100vh;
    box-sizing: border-box;
  }

  .page-content {
    height: 100%;
    box-sizing: border-box;
    background: #f8f8f8;
    display: flex;
    flex-direction: column;

    .top-wrapper {
      display: block;
      flex-grow: 0;
      padding: 10px 10px 0;
    }

    .top {
      position: relative;
      display: flex;

      .btn-order,
      .btn-promo {
        flex: 1;
        background: #ffffff;
        border-radius: 4px;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: center;
        padding: 10px 18px;

        .icon-area {
          position: relative;

          .badge {
            position: absolute;
            top: 0;
            right: 0;
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: #ff3e3e;
            color: #fff;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            transform: scale(0.8);
          }

          .icon {
            width: 47px;
            height: 47px;
            display: block;
          }
        }

        .title {
          margin-top: 2px;
          margin-bottom: 0;
          font-weight: 600;
          font-size: 14px;
          color: #333333;
          line-height: 20px;
        }

        .desc {
          font-weight: 400;
          font-size: 12px;
          color: #666666;
          line-height: 14px;
          text-align: center;
          min-height: 28px;

          :deep(b) {
            color: #ff3c3c;
          }
        }
      }

      .btn-promo {
        margin-left: 10px;
      }
    }

    .message-box {
      flex: 1;
      min-height: 0;

      &.message-list {
        margin: 10px;
        border-radius: 4px;
        overflow: hidden;
      }
    }
  }

  .page-content-room {
    background-color: #fff;

    .top-wrapper {
      padding-top: 0;
    }
  }

  .page-content-pc {
    background-color: #fff;

    .top-wrapper {
      .top {
        border-bottom: 1px solid #f1f1f1;
      }
    }
  }
}

:deep(.van-notify) {
  width: 308px;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 15px;
  border-radius: 10px;
  background: black;
  color: white;
  opacity: 0.7;
}

.out-room-list {
  padding: 0 7px 5px;
  background-color: #fff;
  margin-bottom: 10px;
  min-height: 60px;
}

.pc-top-list {
  padding: 0 7px 5px;
  background-color: #fff;
  border-bottom: 1px solid #f1f1f1;
}
</style>
